import { useMemo } from "react";

import clsx from "clsx";
import { isNil } from "lodash";

import { TertiaryButton } from "@components/v2";
import {
  type ValueFloatComparisonInput,
  type ValueIntComparisonInput,
} from "@north-beam/nb-client-side";

import { DEFAULT_FILTERS } from "./filter-modal";
import { OrderFilters, useOrdersQueryParams } from "./hooks/use-orders-filters";

const METRIC_FILTER_OPERATOR: Record<string, string> = {
  equalsTo: "=",
  greaterThan: ">",
  greaterThanEqualTo: ">=",
  lessThan: "<",
  lessThanOrEqualTo: "<=",
};

interface Props {
  onModalOpen: () => void;
}

export const MetricFilters = ({ onModalOpen }: Props) => {
  const [state, setState] = useOrdersQueryParams();
  const hasFilters = useMemo(
    () =>
      // String filters
      state.orderId ||
      state.orderNumber ||
      state.customerId ||
      // Array filters
      (state.orderTags?.length ?? 0) > 0 ||
      (state.orderTypes?.length ?? 0) > 0 ||
      (state.products?.length ?? 0) > 0 ||
      (state.subscriptions?.length ?? 0) > 0 ||
      (state.sourceName?.length ?? 0) > 0 ||
      (state.customerTags?.length ?? 0) > 0 ||
      (state.attributed?.length ?? 0) > 0 ||
      (state.discountCodes?.length ?? 0) > 0 ||
      // Value comparison filters (float)
      !isNil(state.orderValue) ||
      !isNil(state.discountValue) ||
      !isNil(state.shippingValue) ||
      !isNil(state.taxValue) ||
      !isNil(state.refundValue) ||
      // Value comparison filters (int)
      !isNil(state.numberOfOriginalItems) ||
      !isNil(state.numberOfTouchpoints),
    [
      // String filters
      state.orderId,
      state.orderNumber,
      state.customerId,
      // Array filters
      state.orderTags,
      state.orderTypes,
      state.products,
      state.subscriptions,
      state.sourceName,
      state.customerTags,
      state.attributed,
      state.discountCodes,
      // Value comparison filters (float)
      state.orderValue,
      state.discountValue,
      state.shippingValue,
      state.taxValue,
      state.refundValue,
      // Value comparison filters (int)
      state.numberOfOriginalItems,
      state.numberOfTouchpoints,
    ],
  );

  const metricFilters = useMemo(() => {
    const filters: Array<{
      operator: string;
      value: number;
      metricName: string;
      metricId: string;
    }> = [];

    // Handle value comparison filters (float)
    const floatFilters = [
      { id: OrderFilters.orderValue, name: "Order Value" },
      { id: OrderFilters.discountValue, name: "Discount Value" },
      { id: OrderFilters.shippingValue, name: "Shipping Value" },
      { id: OrderFilters.taxValue, name: "Tax Value" },
      { id: OrderFilters.refundValue, name: "Refund Value" },
    ];

    floatFilters.forEach(({ id, name }) => {
      const filter = state[id] as ValueFloatComparisonInput;
      if (filter?.comparisonOperator && !isNil(filter?.value)) {
        filters.push({
          operator: filter.comparisonOperator,
          value: Number(filter.value),
          metricName: name,
          metricId: id,
        });
      }
    });

    // Handle value comparison filters (int)
    const intFilters = [
      {
        id: OrderFilters.numberOfOriginalItems,
        name: "Number of Original Items",
      },
      { id: OrderFilters.numberOfTouchpoints, name: "Number of Touchpoints" },
    ];

    intFilters.forEach(({ id, name }) => {
      const filter = state[id] as ValueIntComparisonInput;
      if (filter?.comparisonOperator && !isNil(filter?.value)) {
        filters.push({
          operator: filter.comparisonOperator,
          value: Number(filter.value),
          metricName: name,
          metricId: id,
        });
      }
    });

    // Handle array filters
    const arrayFilters = [
      { id: OrderFilters.orderTags, name: "Order Tags" },
      { id: OrderFilters.orderTypes, name: "Order Types" },
      { id: OrderFilters.products, name: "Products" },
      { id: OrderFilters.subscriptions, name: "Subscriptions" },
      { id: OrderFilters.sourceName, name: "Source Name" },
      { id: OrderFilters.customerTags, name: "Customer Tags" },
      { id: OrderFilters.attributed, name: "Attributed" },
      { id: OrderFilters.discountCodes, name: "Discount Codes" },
    ];

    arrayFilters.forEach(({ id, name }) => {
      const values = state[id] as string[];
      if (Array.isArray(values) && values.length > 0) {
        filters.push({
          operator: "in",
          value: values.length,
          metricName: name,
          metricId: id,
        });
      }
    });

    // Handle string filters
    const stringFilters = [
      { id: OrderFilters.orderId, name: "Order ID" },
      { id: OrderFilters.orderNumber, name: "Order Number" },
      { id: OrderFilters.customerId, name: "Customer ID" },
    ];

    stringFilters.forEach(({ id, name }) => {
      const hasValue = !isNil(state[id]);
      if (hasValue) {
        filters.push({
          operator: "=",
          value: 1, // For string filters, we just indicate presence
          metricName: name,
          metricId: id,
        });
      }
    });

    return filters;
  }, [state]);

  return (
    <>
      {hasFilters && (
        <MetricButton
          metricFilters={metricFilters}
          onClear={() => setState(DEFAULT_FILTERS)}
          onModalOpen={onModalOpen}
        />
      )}
      {!hasFilters && (
        <TertiaryButton onClick={onModalOpen}>
          FILTER
          <i className="fa-regular fa-filter"></i>
        </TertiaryButton>
      )}
    </>
  );
};

const MetricButton = ({
  metricFilters,
  onClear,
  onModalOpen,
}: {
  metricFilters: {
    metricId: string;
    operator: string;
    value: number;
    metricName: string;
  }[];
  onClear: () => void;
  onModalOpen: () => void;
}) => {
  const { operator, value, metricName } = metricFilters[0];

  return (
    <div
      className={clsx(
        "bg-v2-primary border border-v2-light rounded-md px-2 py-1",
        "text-v2-action text-sm font-semibold tracking-wide",
        "flex items-center cursor-pointer",
        "hover:bg-v2-secondary hover:text-v2-action-hover",
      )}
      onClick={onModalOpen}
    >
      Filter: {metricName} {METRIC_FILTER_OPERATOR[operator]} {value}{" "}
      {metricFilters.length > 1 && `(${metricFilters.length - 1}+)`}
      <i
        className={clsx(
          "fa-regular fa-x text-2xs ml-1 mt-0.5 font-bold rounded-full flex-center h-4.5 w-4.5",
          "hover:bg-v2-overlay-action hover:text-v2-inverse",
        )}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      ></i>
    </div>
  );
};
